import { CircleAlert } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { cn } from '#client/lib/cn';

export interface FieldShellProps {
  label: ReactNode;
  /**
   * Hides the label visually but keeps it for assistive technology. Used inside
   * data tables, where the column header is the visible label.
   */
  hideLabel?: boolean | undefined;
  /** Server-side (Zod) or local validation message. */
  error?: string | undefined;
  helpText?: ReactNode | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
}

interface FormFieldProps extends FieldShellProps {
  children: (props: {
    id: string;
    describedBy?: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

/**
 * Wraps a control with its label, help text and error message, and wires up the
 * `id` / `aria-describedby` / `aria-invalid` relationships.
 */
export function FormField({
  label,
  hideLabel = false,
  error,
  helpText,
  required,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;
  const describedBy = cn(error && errorId, helpText && helpId) || undefined;

  return (
    <div className={cn('c-field', error && 'c-field--invalid', className)}>
      <label className={cn('c-field__label', hideLabel && 'sr-only')} htmlFor={id}>
        {label}
        {required && (
          <span className="c-field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {helpText && (
        <p className="c-field__help" id={helpId}>
          {helpText}
        </p>
      )}
      {error && (
        // The icon matters: a red message is invisible to a red-green colour
        // deficiency, and the surrounding label gives no hint that anything is
        // wrong. Announced assertively so it interrupts rather than waiting.
        <p className="c-field__error" id={errorId} role="alert">
          <CircleAlert aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
